import React, { useEffect, useMemo, useState } from 'react';
import { Container, Form } from 'react-bootstrap';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import drivingApi from 'api/drivingApi';
import { PATH } from 'constants/path';
import { toastWrapper } from 'utils';
import {
  AuthCard,
  Alert,
  PrimaryButton,
  Spinner,
  TextInput,
  Label,
  colors,
  radius,
  spacing,
  typography,
} from 'design-system';

// Editable: HV nhập giá trị đúng. Flag-only (portraitUrl): chỉ tick
// "chưa đúng", không gửi giá trị mới — admin xử lý ngoài luồng.
// (Hạng dự sát hạch + Khoá học đã được ẩn khỏi UI — admin chỉnh trực
// tiếp ngoài luồng, không cho HV tự báo qua page này.)
const FIELDS = [
  { key: 'name', label: 'Họ và tên', editable: true },
  { key: 'dob', label: 'Ngày sinh', editable: true, type: 'date' },
  { key: 'gender', label: 'Giới tính', editable: true, type: 'select', options: ['Nam', 'Nữ'] },
  { key: 'cardNumber', label: 'Số CCCD', editable: true },
  { key: 'residence', label: 'Địa chỉ thường trú', subtitle: 'Địa chỉ thường trú theo VNEID cấp độ 2', editable: true },
  { key: 'portraitUrl', label: 'Ảnh chân dung', editable: false, type: 'image' },
  // Danh sách GPLX đã có — editable. HV có thể sửa từng entry, thêm
  // mới (tối đa 3) hoặc xoá; submit gửi toàn bộ array. licenseClass
  // chọn từ danh sách hạng bằng visible của driving type.
  { key: 'existingLicenses', label: 'Giấy phép lái xe đã có', editable: true, type: 'license-list', maxItems: 3 },
];

// UI key → backend payload key. Hiện UI key = backend key cho mọi
// trường còn lại; PAYLOAD_KEY giữ dạng map rỗng để dễ thêm trường
// cần đổi tên sau này.
const PAYLOAD_KEY = {};

const STEP = { REVIEW: 'review', EDIT: 'edit' };
const MAX_LICENSES = 3;

// dob trong driving có thể là Date object, ISO string, "YYYY-MM-DD",
// "DD/MM/YYYY", hoặc "Invalid Date" (do legacy import). Chuẩn hoá hiển
// thị thành dd/mm/yyyy; nếu không parse được trả "—".
function formatDobDisplay(value) {
  if (!value && value !== 0) return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '—';
    const d = String(value.getDate()).padStart(2, '0');
    const m = String(value.getMonth() + 1).padStart(2, '0');
    return `${d}/${m}/${value.getFullYear()}`;
  }
  const str = String(value).trim();
  // Bắt cả "Invalid Date" + chỉ "Invalid" + các biến thể từ
  // `new Date(badInput).toString()` đã ghi vào DB.
  if (!str || /invalid/i.test(str)) return '—';
  // ISO Z có giờ (vd "2024-01-02T17:00:00.000Z"): KHÔNG được slice 10
  // ký tự đầu — wall-clock VN có thể là ngày kế tiếp do data legacy lưu
  // UTC. Parse qua Date rồi đọc local TZ components.
  if (/^\d{4}-\d{2}-\d{2}T.*Z$/i.test(str)) {
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${dd}/${mm}/${d.getFullYear()}`;
    }
  }
  // YYYY-MM-DD thuần (data đã clean, no time) — slice trực tiếp.
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  // dd/mm/yyyy đã đúng dạng
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return str;
  // YYYYMMDD
  if (/^\d{8}$/.test(str)) return `${str.slice(6, 8)}/${str.slice(4, 6)}/${str.slice(0, 4)}`;
  // Last-resort: thử new Date(str). Nếu vẫn fail → trả "—" thay
  // vì để nguyên chuỗi rác ("Invalid", "NaN", junk text) hiện ra UI.
  const d = new Date(str);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }
  return '—';
}

// Đảo ngược của formatDobDisplay: bất kỳ định dạng dob nào → YYYY-MM-DD
// để dùng cho <input type='date'> (HTML chuẩn). Trả '' nếu không
// parse được — input sẽ rỗng, HV phải tự chọn.
function toIsoDate(value) {
  if (!value && value !== 0) return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const str = String(value).trim();
  if (!str || /invalid/i.test(str)) return '';
  // dd/mm/yyyy
  let m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  // ISO Z có giờ — wall-clock VN có thể là ngày kế. Parse qua Date,
  // đọc local TZ components → trả YYYY-MM-DD đúng cho `<input type='date'>`.
  if (/^\d{4}-\d{2}-\d{2}T.*Z$/i.test(str)) {
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${mm}-${dd}`;
    }
  }
  // YYYY-MM-DD thuần (data đã clean, no time).
  m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  // YYYYMMDD
  m = str.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(str);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${mm}-${dd}`;
  }
  return '';
}

function buildCurrent(driving) {
  return {
    name: driving?.name || '',
    dob: formatDobDisplay(driving?.dob),
    gender: driving?.gender || '',
    cardNumber: driving?.cardNumber || '',
    residence: driving?.residence || '',
    portraitUrl: driving?.portraitUrl || '',
    existingLicenses: Array.isArray(driving?.existingLicenses) ? driving.existingLicenses : [],
  };
}

// Giá trị init cho ô input ở bước EDIT. Khác `current` ở chỗ:
//  - dob → YYYY-MM-DD (cho <input type='date'>)
//  - existingLicenses dates → YYYY-MM-DD (cho từng entry)
function buildEditValues(driving) {
  const editLicenses = Array.isArray(driving?.existingLicenses)
    ? driving.existingLicenses.map((l) => ({
        licenseClass: l?.licenseClass || '',
        number: l?.number || '',
        issuedDate: toIsoDate(l?.issuedDate),
      }))
    : [];
  return {
    ...buildCurrent(driving),
    dob: toIsoDate(driving?.dob),
    existingLicenses: editLicenses,
  };
}

function ElearningStudentInfoConfirmationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(STEP.REVIEW);
  const [status, setStatus] = useState(null);
  const [current, setCurrent] = useState({});
  const [flagged, setFlagged] = useState({});
  const [values, setValues] = useState({});
  // Track đã retry refresh signed URL chưa — tránh loop khi ảnh fail
  // load (vd S3 401 do URL hết hạn). 1 lần retry → đủ; nếu vẫn fail
  // sau retry thì giữ broken image, không spam request.
  const [portraitRetried, setPortraitRetried] = useState(false);
  // Danh sách hạng bằng visible — fetch 1 lần để dùng cho
  // dropdown licenseClass trong edit UI của existingLicenses.
  const [drivingTypes, setDrivingTypes] = useState([]);

  const goBackToLearning = () => {
    navigate(PATH.ELEARNING.STUDENT.ROOT, { replace: true });
  };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await drivingApi.getMyInfoConfirmation();
      const data = res?.data || {};
      setStatus(data);
      setCurrent(buildCurrent(data.driving));
      setValues(buildEditValues(data.driving));
      setFlagged({});
      setStep(STEP.REVIEW);
      setPortraitRetried(false); // fresh URL — reset retry cờ
    } catch (err) {
      toastWrapper(err?.response?.data?.message || 'Không tải được thông tin', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch drivingCenterType có visible: true theo center của HV để
  // dựng dropdown hạng bằng cho edit UI của existingLicenses.
  useEffect(() => {
    const centerId = status?.driving?.center?._id || status?.driving?.center;
    if (!centerId) return;
    (async () => {
      try {
        const res = await drivingApi.getVisibleDrivingTypes(centerId);
        const types = res?.data || res?.docs || [];
        setDrivingTypes(Array.isArray(types) ? types : []);
      } catch (_) { /* không chặn page */ }
    })();
  }, [status?.driving?.center]);

  // Auto navigate về trang học khi khoá không còn yêu cầu xác nhận
  // (admin tắt flag). Không auto-back khi HV đã confirm — để HV chủ
  // động thay đổi quyết định.
  useEffect(() => {
    if (!status) return;
    if (!status.requireConfirmation) {
      const t = setTimeout(goBackToLearning, 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleLicenseChange = (index, field, value) => {
    setValues((prev) => {
      const list = [...(prev.existingLicenses || [])];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, existingLicenses: list };
    });
  };

  const handleAddLicense = () => {
    setValues((prev) => {
      const list = [...(prev.existingLicenses || [])];
      const field = FIELDS.find((f) => f.key === 'existingLicenses');
      if (list.length >= (field?.maxItems || 3)) return prev;
      list.push({ licenseClass: '', number: '', issuedDate: '' });
      return { ...prev, existingLicenses: list };
    });
  };

  const handleRemoveLicense = (index) => {
    setValues((prev) => {
      const list = [...(prev.existingLicenses || [])];
      list.splice(index, 1);
      return { ...prev, existingLicenses: list };
    });
  };

  const handleConfirm = async () => {
    if (!window.confirm('Bạn xác nhận tất cả thông tin trên là chính xác?')) return;
    setSaving(true);
    try {
      await drivingApi.confirmMyInfo();
      toastWrapper('Đã xác nhận thông tin', 'success');
      goBackToLearning();
    } catch (err) {
      toastWrapper(err?.response?.data?.message || 'Lỗi khi xác nhận', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleFlag = (key) => {
    setFlagged((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  const flaggedKeys = useMemo(() => Object.keys(flagged), [flagged]);

  const handleSubmitUpdate = async () => {
    if (flaggedKeys.length === 0) {
      toastWrapper('Hãy tick các thông tin chưa đúng trước khi gửi.', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      for (const key of flaggedKeys) {
        const field = FIELDS.find((f) => f.key === key);
        if (!field) continue;
        const payloadKey = PAYLOAD_KEY[key] || key;
        if (field.editable) {
          if (field.type === 'license-list') {
            const arr = Array.isArray(values[key]) ? values[key] : [];
            if (arr.length > (field.maxItems || MAX_LICENSES)) {
              toastWrapper(`Tối đa ${field.maxItems || MAX_LICENSES} GPLX.`, 'warning');
              setSaving(false);
              return;
            }
            for (let i = 0; i < arr.length; i++) {
              if (!arr[i].licenseClass || !String(arr[i].licenseClass).trim()) {
                toastWrapper(`GPLX #${i + 1}: Vui lòng chọn hạng bằng.`, 'warning');
                setSaving(false);
                return;
              }
              if (!arr[i].number || !String(arr[i].number).trim()) {
                toastWrapper(`GPLX #${i + 1}: Vui lòng nhập số GPLX.`, 'warning');
                setSaving(false);
                return;
              }
              if (String(arr[i].number).trim().length > 12) {
                toastWrapper(`GPLX #${i + 1}: Số GPLX tối đa 12 chữ số.`, 'warning');
                setSaving(false);
                return;
              }
              if (!arr[i].issuedDate || !String(arr[i].issuedDate).trim()) {
                toastWrapper(`GPLX #${i + 1}: Vui lòng chọn ngày cấp.`, 'warning');
                setSaving(false);
                return;
              }
            }
            payload[payloadKey] = arr;
          } else {
            const v = (values[key] ?? '').toString().trim();
            if (!v) {
              const verb = field.type === 'select' || field.type === 'date' ? 'chọn' : 'nhập';
              toastWrapper(`Vui lòng ${verb} giá trị mới cho "${field.label}".`, 'warning');
              setSaving(false);
              return;
            }
            if (field.maxLength && v.length > field.maxLength) {
              toastWrapper(`"${field.label}" không được vượt quá ${field.maxLength} ký tự.`, 'warning');
              setSaving(false);
              return;
            }
            // dob: values lưu YYYY-MM-DD (từ <input type='date'>), current
            // lưu dd/mm/yyyy (display). Normalize cả 2 về ISO để so sánh.
            const currentCmp = field.type === 'date'
              ? toIsoDate(current[key])
              : (current[key] ?? '').toString().trim();
            if (v === currentCmp) {
              toastWrapper(`Giá trị "${field.label}" chưa thay đổi so với hiện tại.`, 'warning');
              setSaving(false);
              return;
            }
            payload[payloadKey] = v;
          }
        } else {
          payload[payloadKey] = true;
        }
      }
      await drivingApi.submitMyInfoUpdateRequest(payload);
      toastWrapper('Đã gửi yêu cầu. Vui lòng chờ trung tâm duyệt.', 'success');
      goBackToLearning();
    } catch (err) {
      toastWrapper(err?.response?.data?.message || 'Lỗi khi gửi yêu cầu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageWrap>
        <Container className='py-4 py-md-5'>
          <AuthCard maxWidth={720} title='Xác nhận thông tin cá nhân'>
            <Spinner block label='Đang tải thông tin...' />
          </AuthCard>
        </Container>
      </PageWrap>
    );
  }

  if (!status) {
    return (
      <PageWrap>
        <Container className='py-4 py-md-5'>
          <AuthCard maxWidth={720} title='Xác nhận thông tin cá nhân'>
            <Alert variant='danger'>Không tải được dữ liệu. Vui lòng tải lại trang.</Alert>
          </AuthCard>
        </Container>
      </PageWrap>
    );
  }

  const pending = status.pendingRequest;
  const alreadyConfirmed = !!status.confirmedAt;
  const inEditStep = step === STEP.EDIT;

  return (
    <PageWrap>
      <Container className='py-4 py-md-5'>
        <AuthCard
          maxWidth={720}
          title='Xác nhận thông tin cá nhân'
          subtitle='Kiểm tra các thông tin dưới đây. Nếu đúng, bấm Xác nhận; nếu sai, bấm Cập nhật để gửi yêu cầu chỉnh sửa.'
        >
          {pending && (
            <Alert variant='info'>
              Bạn đang có một yêu cầu cập nhật chờ trung tâm duyệt.
              <div style={{ marginTop: spacing[2], fontSize: typography.size.xs }}>
                <strong>Trường đã báo sai:</strong>{' '}
                {Object.keys(pending.requestedFields || {})
                  .map((k) => FIELDS.find((f) => (PAYLOAD_KEY[f.key] || f.key) === k)?.label || k)
                  .join(', ') || '(không có)'}
              </div>
              {pending.adminNote && (
                <div style={{ marginTop: spacing[1], fontSize: typography.size.xs }}>
                  <strong>Ghi chú admin:</strong> {pending.adminNote}
                </div>
              )}
              <div style={{ marginTop: spacing[2], fontSize: typography.size.xs, color: colors.gray500 }}>
                Bạn vẫn có thể thay đổi quyết định, yêu cầu cũ sẽ bị thay thế khi bạn gửi yêu cầu mới.
              </div>
            </Alert>
          )}

          {alreadyConfirmed && !pending && (
            <Alert variant='success'>
              Bạn đã xác nhận thông tin trước đây. Có thể xác nhận lại hoặc gửi yêu cầu cập nhật nếu cần.
            </Alert>
          )}

          {inEditStep && (
            <Alert variant='warning' title='Báo lỗi thông tin'>
              <div>
                Đánh dấu ô bên cạnh các trường <strong>chưa chính xác</strong>:
              </div>
              <ul style={{ margin: `${spacing[2]} 0 0`, paddingLeft: spacing[5] }}>
                <li>
                  <strong>Họ tên, Ngày sinh, CCCD, Địa chỉ thường trú, Giới tính:</strong>{' '}
                  nhập giá trị đúng vào ô xuất hiện bên dưới.
                </li>
                <li style={{ marginTop: spacing[1] }}>
                  <strong>Ảnh chân dung:</strong>{' '}
                  chỉ đánh dấu, trung tâm sẽ liên hệ để cập nhật.
                </li>
                <li style={{ marginTop: spacing[1] }}>
                  <strong>GPLX đã có:</strong>{' '}
                  nhập thông tin từng GPLX, chọn hạng bằng từ danh sách (tối đa {MAX_LICENSES}).
                </li>
              </ul>
            </Alert>
          )}

          <FieldList>
            {FIELDS.map((f) => {
              const cur = current[f.key] ?? '';
              const isChecked = !!flagged[f.key];
              // Liên kết label ↔ checkbox qua htmlFor để click vào tiêu
              // đề cũng toggle được. Native HTML semantic — không cần
              // onClick handler riêng.
              const checkboxId = `flag-${f.key}`;
              return (
                <FieldRow key={f.key}>
                  {inEditStep && (
                    <CheckCol>
                      <Form.Check
                        id={checkboxId}
                        type='checkbox'
                        checked={isChecked}
                        onChange={() => toggleFlag(f.key)}
                        aria-label={`${f.label} chưa đúng`}
                      />
                    </CheckCol>
                  )}
                  <FieldBody>
                    <Label
                      htmlFor={inEditStep ? checkboxId : undefined}
                      style={inEditStep ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                    >
                      {f.label}
                    </Label>
                    {f.subtitle && <FieldSubtitle>{f.subtitle}</FieldSubtitle>}
                    {f.type === 'license-list' ? (
                      Array.isArray(cur) && cur.length > 0 ? (
                        <LicenseList>
                          {cur.map((lic, idx) => (
                            <LicenseCard key={idx}>
                              <LicenseHeader>
                                <strong>GPLX hạng {lic.licenseClass || '—'}</strong>
                                {lic.number && <code>{lic.number}</code>}
                              </LicenseHeader>
                              <LicenseFields>
                                {lic.issuedDate && (
                                  <span><b>Ngày cấp:</b> {formatDobDisplay(lic.issuedDate)}</span>
                                )}
                              </LicenseFields>
                              {(lic.frontUrl || lic.backUrl) && (
                                <LicenseImages>
                                  {lic.frontUrl && <img src={lic.frontUrl} alt='Mặt trước GPLX' />}
                                  {lic.backUrl && <img src={lic.backUrl} alt='Mặt sau GPLX' />}
                                </LicenseImages>
                              )}
                            </LicenseCard>
                          ))}
                        </LicenseList>
                      ) : (
                        <Muted>(chưa có)</Muted>
                      )
                    ) : f.type === 'image' ? (
                      cur ? (
                        <PortraitImg
                          src={cur}
                          alt={f.label}
                          onError={() => {
                            // S3 trả 401 (signed URL hết hạn 1h) hoặc
                            // bất kỳ lỗi load nào → re-fetch status để
                            // lấy URL mới. Chỉ retry 1 lần để tránh
                            // infinite loop khi URL thực sự broken
                            // (vd object đã bị xoá khỏi S3).
                            if (!portraitRetried && f.key === 'portraitUrl') {
                              setPortraitRetried(true);
                              fetchStatus();
                            }
                          }}
                        />
                      ) : status?.portraitProcessing ? (
                        // JP2 từ XML import chưa được convert sang JPG +
                        // upload lên S3. Job convert-portrait-to-s3 đã được
                        // queue (BE) ngay khi gọi getStatus. Hiển thị
                        // trạng thái "đang xử lý" để HV biết quay lại sau.
                        <ProcessingState>
                          <Spinner size='sm' />
                          <div>
                            <div>Đang xử lý ảnh chân dung...</div>
                            <small>Vui lòng quay lại trang này sau vài phút.</small>
                          </div>
                        </ProcessingState>
                      ) : (
                        <Muted>(chưa có)</Muted>
                      )
                    ) : (
                      <Value>{cur || <Muted>(trống)</Muted>}</Value>
                    )}

                    {inEditStep && isChecked && f.editable && (
                      f.type === 'license-list' ? (
                        <LicenseEditWrapper>
                          {values.existingLicenses && values.existingLicenses.length > 0 ? (
                            values.existingLicenses.map((lic, idx) => (
                              <LicenseEditCard key={idx}>
                                <LicenseEditRow>
                                  <LicenseEditLabel>#{idx + 1}</LicenseEditLabel>
                                  <RemoveBtn
                                    type='button'
                                    onClick={() => handleRemoveLicense(idx)}
                                    title='Xoá GPLX này'
                                  >
                                    ×
                                  </RemoveBtn>
                                </LicenseEditRow>
                                <LicenseEditFields>
                                  <LicenseEditField>
                                    <small>Hạng bằng *</small>
                                    <Form.Select
                                      size='sm'
                                      value={lic.licenseClass || ''}
                                      onChange={(e) => handleLicenseChange(idx, 'licenseClass', e.target.value)}
                                    >
                                      <option value='' disabled>-- Chọn hạng --</option>
                                      {drivingTypes.map((ct) => (
                                        <option key={ct.drivingType?._id || ct._id} value={ct.drivingType?.label || ct.label}>
                                          {ct.drivingType?.label || ct.label}{ct.drivingType?.description ? ` (${ct.drivingType.description})` : ''}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </LicenseEditField>
                                  <LicenseEditField>
                                    <small>Số GPLX *</small>
                                    <TextInput
                                      placeholder='VD: 040123456789'
                                      maxLength={12}
                                      inputMode='numeric'
                                      value={lic.number || ''}
                                      onChange={(e) => handleLicenseChange(idx, 'number', e.target.value.replace(/\D/g, ''))}
                                    />
                                  </LicenseEditField>
                                  <LicenseEditField>
                                    <small>Ngày cấp *</small>
                                    <TextInput
                                      type='date'
                                      value={lic.issuedDate || ''}
                                      onChange={(e) => handleLicenseChange(idx, 'issuedDate', e.target.value)}
                                    />
                                  </LicenseEditField>
                                </LicenseEditFields>
                              </LicenseEditCard>
                            ))
                          ) : null}
                          {(!values.existingLicenses || values.existingLicenses.length < (f.maxItems || MAX_LICENSES)) && (
                            <AddLicenseBtn type='button' onClick={handleAddLicense}>
                              + Thêm GPLX
                            </AddLicenseBtn>
                          )}
                        </LicenseEditWrapper>
                      ) : f.type === 'select' ? (
                        <Form.Select
                          size='sm'
                          required
                          style={{ marginTop: spacing[2] }}
                          value={values[f.key] || ''}
                          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                        >
                          {/* Disabled placeholder ép HV phải chọn 1 option
                              hợp lệ — submit logic block khi value rỗng. */}
                          <option value='' disabled>-- Chọn {f.label.toLowerCase()} --</option>
                          {f.options.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </Form.Select>
                      ) : (
                        <div style={{ marginTop: spacing[2] }}>
                          <TextInput
                            type={f.type === 'date' ? 'date' : 'text'}
                            placeholder={f.placeholder || (f.type === 'date' ? '' : `Nhập ${f.label.toLowerCase()} đúng`)}
                            maxLength={f.maxLength}
                            value={values[f.key] || ''}
                            onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                          />
                        </div>
                      )
                    )}

                    {inEditStep && isChecked && !f.editable && (
                      <FlagHint>
                        Đã đánh dấu chưa đúng. Trung tâm sẽ liên hệ để xử lý.
                      </FlagHint>
                    )}
                  </FieldBody>
                </FieldRow>
              );
            })}
          </FieldList>

          <Actions>
            {step === STEP.REVIEW ? (
              <>
                <PrimaryButton
                  variant='secondary'
                  onClick={() => {
                    setStep(STEP.EDIT);
                    // Scroll lên đầu để HV đọc hướng dẫn báo lỗi trước khi
                    // tick. Trên mobile đặc biệt cần — banner hướng dẫn
                    // mới hiện ra ở trên cùng.
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={saving}
                >
                  Cập nhật
                </PrimaryButton>
                <PrimaryButton variant='success' onClick={handleConfirm} loading={saving} loadingText='Đang xác nhận...'>
                  Xác nhận
                </PrimaryButton>
              </>
            ) : (
              <>
                <PrimaryButton
                  variant='secondary'
                  onClick={() => { setStep(STEP.REVIEW); setFlagged({}); setValues(buildEditValues(status.driving)); }}
                  disabled={saving}
                >
                  Quay lại
                </PrimaryButton>
                <PrimaryButton onClick={handleSubmitUpdate} loading={saving} loadingText='Đang gửi...'>
                  Gửi yêu cầu cập nhật
                </PrimaryButton>
              </>
            )}
          </Actions>
        </AuthCard>
      </Container>
    </PageWrap>
  );
}

// Mobile-first breakpoint. Bootstrap default sm = 576px; xài 640px
// (Tailwind sm) cho dễ nhớ trong design system.
const MOBILE = '@media (max-width: 640px)';

const PageWrap = styled.div`
  min-height: 100vh;
  background: ${colors.background};
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-top: ${spacing[4]};

  ${MOBILE} {
    gap: ${spacing[2]};
    margin-top: ${spacing[3]};
  }
`;

const FieldRow = styled.div`
  display: flex;
  gap: ${spacing[3]};
  align-items: flex-start;
  padding: ${spacing[3]};
  background: ${colors.gray50};
  border: 1px solid ${colors.border};
  border-radius: ${radius.md};

  ${MOBILE} {
    padding: ${spacing[3]} ${spacing[2]};
    gap: ${spacing[2]};
  }
`;

const CheckCol = styled.div`
  display: flex;
  align-items: center;
  padding-top: 2px;
  flex-shrink: 0;
`;

const FieldBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const Value = styled.div`
  font-size: ${typography.size.base};
  color: ${colors.gray900};
  word-break: break-word;

  ${MOBILE} {
    font-size: ${typography.size.sm};
  }
`;

const Muted = styled.span`
  color: ${colors.gray400};
  font-style: italic;
`;

// Subtitle ngay dưới Label — ghi chú nguồn dữ liệu (vd VNEID) hoặc
// gợi ý ngắn. Cỡ chữ nhỏ hơn, màu nhạt để không "cướp" sự chú ý
// của Label / Value.
const FieldSubtitle = styled.div`
  margin-top: -${spacing[1]};
  margin-bottom: ${spacing[2]};
  font-size: ${typography.size.xs};
  color: ${colors.gray500};
  line-height: ${typography.leading.normal};
`;

const PortraitImg = styled.img`
  max-width: 140px;
  max-height: 180px;
  object-fit: cover;
  border-radius: ${radius.md};
  border: 1px solid ${colors.border};

  ${MOBILE} {
    max-width: 110px;
    max-height: 140px;
  }
`;

// JP2 (từ XML import của cục) chưa convert sang JPG/S3 — backend đã
// queue job ngầm. Hiển thị state này cho HV biết KHÔNG phải lỗi mất
// ảnh.
const ProcessingState = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  background: ${colors.warningSoft || '#fef3c7'};
  border: 1px dashed ${colors.warning || '#d97706'};
  border-radius: ${radius.md};
  font-size: ${typography.size.sm};
  color: ${colors.gray700};

  small {
    display: block;
    color: ${colors.gray500};
    margin-top: ${spacing[1]};
  }
`;

const FlagHint = styled.div`
  margin-top: ${spacing[2]};
  font-size: ${typography.size.xs};
  color: ${colors.info};
`;

// Danh sách GPLX đã có — list các card hiển thị ở bước REVIEW.
const LicenseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const LicenseCard = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.md};
  padding: ${spacing[3]};
  font-size: ${typography.size.sm};
`;

const LicenseHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[2]};

  code {
    font-size: ${typography.size.xs};
    background: ${colors.gray100};
    padding: 2px 6px;
    border-radius: 4px;
    color: ${colors.gray700};
  }
`;

const LicenseFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  color: ${colors.gray700};

  b {
    color: ${colors.gray500};
    font-weight: ${typography.weight.medium};
    margin-right: ${spacing[1]};
  }
`;

const LicenseImages = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};

  img {
    max-width: 160px;
    max-height: 100px;
    object-fit: cover;
    border-radius: ${radius.sm};
    border: 1px solid ${colors.border};
  }

  ${MOBILE} {
    img { max-width: 130px; max-height: 80px; }
  }
`;

// Edit UI cho license-list — HV có thể thêm/sửa/xoá GPLX (tối đa 3).
const LicenseEditWrapper = styled.div`
  margin-top: ${spacing[2]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const LicenseEditCard = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.md};
  padding: ${spacing[3]};
`;

const LicenseEditRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing[2]};
`;

const LicenseEditLabel = styled.span`
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.sm};
  color: ${colors.gray700};
`;

const RemoveBtn = styled.button`
  background: none;
  border: 1px solid ${colors.border};
  border-radius: ${radius.sm};
  color: ${colors.gray500};
  font-size: 18px;
  line-height: 1;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: ${colors.gray100};
    color: ${colors.danger || '#dc3545'};
  }
`;

const LicenseEditFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const LicenseEditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 140px;
  flex: 1;

  small {
    font-size: ${typography.size.xs};
    color: ${colors.gray500};
    font-weight: ${typography.weight.medium};
  }

  ${MOBILE} {
    min-width: 100%;
    flex: none;
  }
`;

const AddLicenseBtn = styled.button`
  background: none;
  border: 1px dashed ${colors.border};
  border-radius: ${radius.md};
  padding: ${spacing[2]};
  color: ${colors.info || '#0d6efd'};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  &:hover {
    background: ${colors.gray50};
    border-color: ${colors.info || '#0d6efd'};
  }
`;

// Desktop: căn phải, buttons inline. Mobile: stack đầy chiều ngang
// (PrimaryButton tự hỗ trợ `block` prop, ở đây ép qua CSS để không
// phải đổi JSX theo media).
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${spacing[2]};
  margin-top: ${spacing[5]};
  padding-top: ${spacing[4]};
  border-top: 1px solid ${colors.border};

  ${MOBILE} {
    flex-direction: column-reverse;
    gap: ${spacing[2]};

    /* Mọi action button full-width trên mobile cho dễ chạm. */
    > * {
      width: 100%;
    }
  }
`;

export default ElearningStudentInfoConfirmationPage;
